/**
 * Meanbook Note - Packer Management & Picker Selection Module
 * จัดการรายชื่อพนักงานแพ็คของ (Packers List) และหน้าต่างเลือกผู้รับผิดชอบการแพ็ค (Packer Selection Modal)
 */

import { collection, doc, addDoc, deleteDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.js";
import { hasRole } from "./auth.js";
import { escapeHTML, showToast, showConfirm } from "./uiHelpers.js";

let allPackers = [];
let selectedPackerOrder = null;
let selectedPackerName = "";

export function setPackersList(packers) {
  allPackers = Array.isArray(packers) ? packers : [];
  renderPackersList();
}

export function getPackersList() {
  return allPackers;
}

export function getSelectedPackerOrder() {
  return selectedPackerOrder;
}

export function renderPackersList() {
  const ul = document.getElementById("packers-list-ul");
  if (!ul) return;

  ul.innerHTML = "";

  if (allPackers.length === 0) {
    ul.innerHTML = `
      <li style="padding: 16px; text-align: center; color: var(--color-text-muted); font-size: 0.8rem;">
        ไม่มีรายชื่อคนแพ็คของในขณะนี้
      </li>
    `;
    return;
  }

  allPackers.forEach(packer => {
    const li = document.createElement("li");
    li.style.display = "flex";
    li.style.justifyContent = "space-between";
    li.style.alignItems = "center";
    li.style.padding = "8px 16px";
    li.style.borderBottom = "1px solid var(--border-color)";
    li.style.color = "var(--color-text-main)";
    li.style.fontSize = "0.85rem";

    li.innerHTML = `
      <span style="font-weight: 500;">${escapeHTML(packer.name)}</span>
      <button type="button" class="btn btn-icon btn-danger btn-delete-packer" data-id="${packer.id}" data-name="${escapeHTML(packer.name)}" style="padding: 4px; border-radius: 4px; background: transparent; border: none; color: var(--color-danger); cursor: pointer; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px;">
        <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
      </button>
    `;

    ul.appendChild(li);
  });

  // Bind click handlers for delete buttons
  ul.querySelectorAll(".btn-delete-packer").forEach(btn => {
    btn.addEventListener("click", () => {
      const packerId = btn.getAttribute("data-id");
      const packerName = btn.getAttribute("data-name");
      deletePacker(packerId, packerName);
    });
  });

  if (window.lucide) window.lucide.createIcons();
}

export async function addPacker() {
  const input = document.getElementById("setting-new-packer-name");
  if (!input) return;

  const name = input.value.trim();
  if (!name) {
    showToast("กรุณาระบุชื่อคนแพ็คของ", "danger");
    return;
  }

  // Check if name already exists
  const exists = allPackers.some(p => p.name.toLowerCase() === name.toLowerCase());
  if (exists) {
    showToast("มีรายชื่อคนแพ็คของนี้อยู่ในระบบแล้ว", "danger");
    return;
  }

  try {
    await addDoc(collection(db, "packers"), {
      name: name,
      createdAt: serverTimestamp()
    });
    input.value = "";
    showToast("เพิ่มรายชื่อคนแพ็คของสำเร็จ!");
  } catch (err) {
    console.error("Error adding packer:", err);
    showToast("เกิดข้อผิดพลาดในการบันทึกข้อมูล", "danger");
  }
}

export async function deletePacker(id, name) {
  showConfirm("ยืนยันการลบรายชื่อ", `คุณต้องการลบรายชื่อ "${name}" ใช่หรือไม่?`, async () => {
    try {
      await deleteDoc(doc(db, "packers", id));
      showToast("ลบรายชื่อพนักงานแพ็คของสำเร็จ!");
    } catch (err) {
      console.error("Error deleting packer:", err);
      showToast("เกิดข้อผิดพลาดในการลบข้อมูล", "danger");
    }
  });
}

export function openPackerSelectModal(order) {
  selectedPackerOrder = order;
  selectedPackerName = "";

  const modal = document.getElementById("packer-select-modal");
  const listContainer = document.getElementById("packer-select-list");
  const saveBtn = document.getElementById("btn-packer-save");

  if (!modal || !listContainer || !saveBtn) return;

  const btnGotoManage = document.getElementById("btn-goto-manage-packers");
  if (btnGotoManage) {
    if (hasRole(["Super Admin", "Packer & Picker"])) {
      btnGotoManage.style.display = "inline-flex";
    } else {
      btnGotoManage.style.display = "none";
    }
  }

  saveBtn.disabled = true;
  listContainer.innerHTML = "";

  if (allPackers.length === 0) {
    listContainer.innerHTML = `
      <div style="text-align: center; color: var(--color-text-muted); font-size: 0.85rem; padding: 16px;">
        กรุณาเพิ่มรายชื่อคนแพ็คของในฟันเฟือง Setting ก่อนเลือกใช้งานครับ
      </div>
    `;
    modal.style.display = "flex";
    return;
  }

  allPackers.forEach(packer => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "packer-select-option-btn";
    btn.style.width = "100%";
    btn.style.padding = "10px 14px";
    btn.style.border = "1px solid var(--border-color)";
    btn.style.borderRadius = "8px";
    btn.style.background = "var(--bg-card)";
    btn.style.color = "var(--color-text-main)";
    btn.style.fontSize = "0.85rem";
    btn.style.fontWeight = "500";
    btn.style.textAlign = "left";
    btn.style.cursor = "pointer";
    btn.style.transition = "all 0.2s ease";
    btn.style.display = "flex";
    btn.style.justifyContent = "space-between";
    btn.style.alignItems = "center";

    btn.innerHTML = `
      <span>${escapeHTML(packer.name)}</span>
      <i class="check-icon" data-lucide="check" style="width: 14px; height: 14px; opacity: 0; transition: opacity 0.2s; color: var(--color-success);"></i>
    `;

    btn.addEventListener("click", () => {
      // Clear previous active states
      listContainer.querySelectorAll(".packer-select-option-btn").forEach(opt => {
        opt.style.borderColor = "var(--border-color)";
        opt.style.background = "var(--bg-card)";
        const icon = opt.querySelector(".check-icon");
        if (icon) icon.style.opacity = "0";
      });

      // Highlight selected
      btn.style.borderColor = "var(--color-primary)";
      btn.style.background = "rgba(224, 189, 137, 0.1)"; // Warm golden hue
      const checkIcon = btn.querySelector(".check-icon");
      if (checkIcon) checkIcon.style.opacity = "1";

      selectedPackerName = packer.name;
      saveBtn.disabled = false;
    });

    listContainer.appendChild(btn);
  });

  if (window.lucide) window.lucide.createIcons();

  modal.style.display = "flex";
}

export function reopenPackerSelectModal() {
  if (selectedPackerOrder) {
    openPackerSelectModal(selectedPackerOrder);
  }
}

export function setupPackerModal(options = {}) {
  const { onShowSettings } = options;

  const btnAddPacker = document.getElementById("btn-add-packer");
  if (btnAddPacker) btnAddPacker.addEventListener("click", addPacker);

  const inputNewPacker = document.getElementById("setting-new-packer-name");
  if (inputNewPacker) {
    inputNewPacker.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addPacker();
      }
    });
  }

  const btnGotoManagePackers = document.getElementById("btn-goto-manage-packers");
  if (btnGotoManagePackers) {
    btnGotoManagePackers.addEventListener("click", () => {
      const modal = document.getElementById("packer-select-modal");
      if (modal) modal.style.display = "none";
      if (typeof onShowSettings === "function") {
        onShowSettings();
      }
    });
  }

  const btnPackerCancel = document.getElementById("btn-packer-cancel");
  if (btnPackerCancel) {
    btnPackerCancel.addEventListener("click", () => {
      const modal = document.getElementById("packer-select-modal");
      if (modal) modal.style.display = "none";
      selectedPackerOrder = null;
      selectedPackerName = "";
    });
  }

  const btnPackerSave = document.getElementById("btn-packer-save");
  if (btnPackerSave) {
    btnPackerSave.addEventListener("click", async () => {
      if (!selectedPackerOrder || !selectedPackerName) return;

      const orderId = selectedPackerOrder.id;
      const deliveryType = selectedPackerOrder.deliveryType;

      const modal = document.getElementById("packer-select-modal");
      if (modal) modal.style.display = "none";

      if (deliveryType === 'pickup' || deliveryType === 'messenger') {
        const emoji = deliveryType === 'pickup' ? "🏪" : "🚗";
        try {
          await updateDoc(doc(db, "orders", orderId), {
            readyToShip: true,
            isPacked: true,
            pickupDone: false,
            packerName: selectedPackerName,
            updatedAt: serverTimestamp()
          });
          showToast(`แพ็คสำเร็จโดยคุณ ${selectedPackerName} รอรับหน้าร้าน/เรียกรถ ${emoji}`);
        } catch (err) {
          console.error("Pickup packer save error:", err);
          showToast("เกิดข้อผิดพลาดในการบันทึก", "danger");
        }
      } else {
        // Express behavior
        try {
          await updateDoc(doc(db, "orders", orderId), {
            readyToShip: true,
            isPacked: true,
            packerName: selectedPackerName,
            updatedAt: serverTimestamp()
          });
          showToast(`แพ็คสำเร็จโดยคุณ ${selectedPackerName} รอเลขออเดอร์ 🚚`);
        } catch (err) {
          console.error("Express packer save error:", err);
          showToast("เกิดข้อผิดพลาดในการบันทึก", "danger");
        }
      }

      selectedPackerOrder = null;
      selectedPackerName = "";
    });
  }
}
